// NIGHT-DIRECTSTAFF-2026-10-17 §4: "Mark as test" -- Matt's own test submissions, out of the way.
//
// Columns (migrations/2026-10-17-leads-test-mark.sql, filed for the hub, additive):
//   leads.test_marked_at  NULL = a real lead; a timestamptz = marked as a test then
//   leads.test_marked_by  the staff email that marked it
//
// A marked lead is HIDDEN from the lists and counts by default -- the Leads list, Needs a human, the nav badges, the
// "+N new" counts, Conversations -- and a "Show tests" toggle (kept per device) brings them all back. Nothing is ever
// deleted; "Not a test" undoes it. The agents window's notifications read the same column and skip a marked lead.
//
// LIKE spam_marked_at, THE COLUMN MAY NOT BE THERE YET: one probe per session, and the action and the toggle are hidden
// (not greyed) until it answers present -- a button whose write cannot land must never make Matt think a lead was marked.

export const TEST_COLUMNS = ['test_marked_at', 'test_marked_by'];

// DAY-DIRECTSTAFF-2026-11-09 §1 (QA R1 · B): test data by its own address, no mark needed -- an email at a reserved test domain
// (RFC 2606/6761: example.com / .net / .org, *.test, *.example, *.invalid, *.localhost) or a name starting "TEST" (QA's "TEST QA
// 2026-11-03 …", the facilities window's "TEST walk N"). Neither can be a real person. Such a row is read as marked (test_marked_at =
// when it came in) so EVERY rule that leaves marked rows out -- Today, Numbers, the funnel, the lists, the exports -- leaves it out
// too; test_auto says why, and nothing is written. (555-01xx fiction numbers are not a rule here: Numbers already leaves them out.)
// The browser harness's synthetic rows use those reserved addresses on purpose, so a per-device switch (localStorage
// crm.testAddresses.v1 = "show", no button) reads rows as they come; the harness sets it, and its 11-09 block proves the hiding.
const TEST_DOMAIN = /@(?:[^@\s]+\.)?example\.(?:com|net|org)$|\.(?:test|example|invalid|localhost)$/i;
export const TEST_ADDRESSES_KEY = 'crm.testAddresses.v1';
/** '' | why the row is test data by its address: "an example.com address" | 'a "TEST" name' */
export function testAddress(row) {
  if (!row) return '';
  const email = String(row.email || '').trim();
  if (TEST_DOMAIN.test(email)) return `an ${email.slice(email.lastIndexOf('@') + 1).toLowerCase()} address`.replace(/^an ([^aeiou])/, 'a $1');
  if (/^TEST\b/.test(String(row.name || '').trim())) return 'a "TEST" name';
  return '';
}
const shown = (storage) => { try { return !!storage && storage.getItem(TEST_ADDRESSES_KEY) === 'show'; } catch (e) { return false; } };
/** A loaded row, read as test when its address says so (never when it is already marked; nothing written). */
export function withTestAddress(row, storage = typeof localStorage !== 'undefined' ? localStorage : null) {
  if (!row || isTestMarked(row) || shown(storage)) return row;
  const why = testAddress(row);
  return why ? { ...row, test_marked_at: row.created_at || '1970-01-01T00:00:00Z', test_marked_by: null, test_auto: why } : row;
}
export const SHOW_TESTS_KEY = 'crm.showTests.v1';

/** True only for a usable timestamp: '' or junk reads as NOT marked (the lead stays visible, never silently hidden). */
export function isTestMarked(lead) {
  const v = lead && lead.test_marked_at;
  if (v === null || v === undefined || v === '') return false;
  return !Number.isNaN(new Date(v).getTime());
}

/** The PATCH for a mark / an unmark. now() is the browser's clock, as for spam_marked_at. */
export function testMarkPatch(marked, by = '', now = () => new Date()) {
  return marked ? { test_marked_at: now().toISOString(), test_marked_by: String(by || '').slice(0, 320) || null }
    : { test_marked_at: null, test_marked_by: null };
}

/** The leads the CRM lists and counts: without the tests, unless "Show tests" is on. */
export function withoutTests(leads, showTests) {
  return showTests ? leads || [] : (leads || []).filter((l) => !isTestMarked(l));
}
export const testCount = (leads) => (leads || []).filter(isTestMarked).length;

/** Per device. */
export function readShowTests(storage) {
  try { return !!(storage && storage.getItem(SHOW_TESTS_KEY) === '1'); } catch { return false; }
}
export function writeShowTests(storage, on) {
  try { if (storage) storage.setItem(SHOW_TESTS_KEY, on ? '1' : '0'); } catch { /* private window */ }
}

/** One probe: are the columns there? Anything that errors reads as absent (the safe direction: hidden). */
export async function probeTestColumns(client) {
  try {
    const { error } = await client.from('leads').select(TEST_COLUMNS.join(','), { head: true }).limit(0);
    return !error;
  } catch { return false; }
}

/**
 * Mark (or unmark) one lead. Asks for the updated id back: with RLS on, an update the policy refuses is not an error but
 * zero rows, and Matt must never be told a lead was marked when nothing was written. -> the patch applied.
 */
export async function markLeadTest(client, leadId, marked, by = '', now = () => new Date()) {
  const patch = testMarkPatch(marked, by, now);
  const { data, error } = await client.from('leads').update(patch).eq('id', leadId).select('id');
  if (error) throw new Error(error.message || 'Could not save');
  if (!Array.isArray(data) || data.length !== 1) throw new Error('Not saved: the database did not accept the change (are you signed in?)');
  return patch;
}

// DAY-DIRECTSTAFF-2026-10-28 (the agents' test-lead audit): marking a lead as a test also closes its open alerts, the way the hub's
// test-mark scripts do -- else they keep counting in the brief (Waiting on staff, open escalations) and can be re-alerted. An alert
// is the lead's when its detail ends lead_id=<this lead> or it sits on a thread with this lead's phone. Not a test reopens exactly
// the ones the mark closed (handled_by 'test_marked'), nothing else.
export const TEST_MARKED_BY = 'test_marked';
const digits10 = (p) => String(p || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
const leadIdOf = (n) => { const m = /lead_id=([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\s*$/i.exec(String((n && n.detail) || '')); return m ? m[1].toLowerCase() : null; };
/** -> the ids to close (marked) or to reopen (unmarked) */
export function testAlertIds(lead, { notifications = [], conversations = [] } = {}, marked = true) {
  if (!lead) return [];
  const id = String(lead.id || '').toLowerCase();
  const ph = digits10(lead.phone || (lead.raw_payload && lead.raw_payload.phone));
  const threads = new Set((conversations || []).filter((c) => ph.length === 10 && digits10(c.candidate_phone) === ph).map((c) => c.id));
  return (notifications || []).filter((n) => {
    if (!n || !(leadIdOf(n) === id || (n.conversation_id && threads.has(n.conversation_id)))) return false;
    return marked ? String(n.status || 'open') === 'open' && !n.handled_at : n.handled_by === TEST_MARKED_BY;
  }).map((n) => n.id);
}
/** The write: only rows still open (marked) / still closed by the mark (unmarked) -> { ids, patch } of what changed */
export async function testAlertsWrite(client, ids, marked, now = () => new Date()) {
  if (!ids || !ids.length) return { ids: [], patch: null };
  const patch = marked ? { status: 'resolved', handled_by: TEST_MARKED_BY, handled_at: now().toISOString() } : { status: 'open', handled_by: null, handled_at: null };
  const q = client.from('agent_notifications').update(patch).in('id', ids);
  const { data, error } = await (marked ? q.is('handled_at', null) : q.eq('handled_by', TEST_MARKED_BY)).select('id');
  if (error) throw new Error(error.message || 'Alerts not updated');
  return { ids: (data || []).map((x) => x.id), patch };
}

/** "test · Oct 16" for the row's pill. */
export function testLabel(lead) {
  if (!isTestMarked(lead)) return '';
  if (lead.test_auto) return 'test · by its address';
  return `test · ${new Date(lead.test_marked_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}
